/**
 * AuthContext wires the module-level `authStore` into the React tree so
 * components re-render when sign-in / sign-out happens. The store is still the
 * source of truth — this context just publishes ticks, exposes the derived
 * operator identity, and registers the store's two callbacks: sign-out (with
 * its `SessionEndReason`) and the proactive-refresh handler.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  getAccessToken,
  getClaims,
  registerProactiveRefresh,
  registerSignOutCallback,
  signOut as storeSignOut,
  type SessionEndReason,
} from './authStore';
import { proactiveRefresh } from './silentRefresh';
import {
  AuthContext,
  deriveOperator,
  type AuthContextValue,
  type AuthStatus,
  type CurrentOperator,
} from './authContextValue';

export interface AuthProviderProps {
  children: ReactNode;
  initialStatus?: AuthStatus;
}

export function AuthProvider({ children, initialStatus = 'booting' }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>(initialStatus);
  const [operator, setOperator] = useState<CurrentOperator | null>(() =>
    deriveOperator(getClaims()),
  );
  const [sessionEnd, setSessionEnd] = useState<SessionEndReason | null>(null);

  useEffect(() => {
    registerSignOutCallback((reason) => {
      setStatus('unauthed');
      setOperator(null);
      // Only a session that ended on its own raises an interstitial; an explicit sign-out
      // (reason null) leaves any earlier one as it was. `revoked` / `misconfigured` are sticky:
      // parallel calls failing on one shared refresh must not downgrade them to `expired`
      // (whose interstitial auto-redirects — H §4.5 forbids that after a revoke).
      if (reason) {
        setSessionEnd((prev) =>
          (prev === 'revoked' || prev === 'misconfigured') && reason === 'expired' ? prev : reason,
        );
      }
    });
    registerProactiveRefresh(proactiveRefresh);
    return () => {
      registerSignOutCallback(null);
      registerProactiveRefresh(null);
    };
  }, []);

  /**
   * Publish the store's current token. Takes no arguments on purpose: the store already holds
   * the token AND its expiry (set by `bootRefresh`), and re-setting it here used to overwrite
   * that expiry with `null` (H §4.2).
   */
  const setAuthed = useCallback(() => {
    const token = getAccessToken();
    setStatus(token ? 'authed' : 'unauthed');
    setOperator(deriveOperator(getClaims()));
    // A token means we're (re-)authenticated — clear any stale interstitial.
    if (token) setSessionEnd(null);
  }, []);

  const handleSignOut = useCallback(() => {
    storeSignOut();
    setStatus('unauthed');
    setOperator(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, operator, sessionEnd, setAuthed, signOut: handleSignOut }),
    [status, operator, sessionEnd, setAuthed, handleSignOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
